import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import logger from './utils/logging';
import analyticsService from './services/analyticsService';

// Context Providers
import { ToastProvider } from './context/ToastContext';
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { AuthProvider } from './context/AuthContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { ReviewProvider } from './context/ReviewContext';
import { TutorialProvider } from './context/TutorialContext';

// Pages
import Home from './pages/Home';
import Gallery from './pages/Gallery';
import About from './pages/About';
import Contact from './pages/Contact';
import { BookConsultationToContactRedirect } from './components/routing/CustomerServiceRedirects';
import Policies from './pages/Policies';
import Auth from './pages/Auth';
import Product from './pages/Product/Product';
import Collection from './pages/Collection/Collection';
import EventsCalendar from './pages/EventsCalendar';
import EventSignup from './pages/EventSignup';
import Content from './pages/Content';
import SocialFeed from './pages/SocialFeed';
import ZeeSocial from './pages/ZeeSocial';
import Cart from './pages/Cart/Cart';
// Admin page removed - desktop app is used instead
// import Admin from './pages/Admin/Admin';
import CheckoutSuccess from './pages/CheckoutSuccess/CheckoutSuccess';
import CustomCheckout from './pages/Checkout/CustomCheckout';
import AfterpayReturn from './pages/Checkout/AfterpayReturn';
import CustomerProfile from './pages/Profile/CustomerProfile';
import CustomerSignup from './pages/Customer/Signup';
import CustomerLogin from './pages/Customer/Login';
import CustomerProfileNew from './pages/Customer/Profile';
import GuidanceConversation from './pages/GuidanceConversation';
import Connect from './pages/Connect';

// Components
import TrippyNavbar from './components/layout/TrippyNavbar';
import SlideOutCart from './components/features/SlideOutCart';
import SlideOutMobileMenu from './components/features/SlideOutMobileMenu';
import MusicPlayer from './components/features/MusicPlayer';
import WelcomeModal from './components/features/WelcomeModal';
import TutorialOverlay from './components/features/TutorialOverlay';
import RainbowChatbot from './components/features/RainbowChatbot';

// Initialize logging
logger.logComponentMount('App', { version: '2.0.0' });

/** When false: no nav link to /social and /social redirects home. SocialFeed page code stays in repo — flip to true to restore “Zee Social”. */
const SHOW_ZEE_SOCIAL_FEED = false;

/** When false: hide Book Events nav and redirect /events-calendar to home. Individual event
 *  pages (/events/:eventSlug) are always reachable — they self-gate server-side (invite-only
 *  "underground" events require a code/referral link; regular events still need isPublic). */
const SHOW_EVENTS = false;

/** When false: hide the Rainbow AI chatbot widget. Flip to true to restore. */
const SHOW_CHATBOT = false;

// Analytics tracker component
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    const pageName = getPageName(location.pathname);
    analyticsService.trackPageView(pageName, location.pathname);
  }, [location]);

  return null;
}

// Helper to get readable page names
function getPageName(pathname) {
  const routes = {
    '/': 'Home',
    '/gallery': 'Gallery',
                    '/events': 'Events',
                    '/events-calendar': 'Book Events',
                    '/book-consultation': 'Book support (contact)',
                    '/content': 'Content',
                    '/social': 'Zee Social Stream',
                    '/zee-social': 'Zee Social',
                    '/about': 'About',
                    '/contact': 'Contact',
    '/policies': 'Policies',
    '/auth': 'Auth',
    '/cart': 'Cart',
    '/checkout': 'Checkout',
        '/checkout/afterpay-return': 'Zee Afterpay Return',
        '/checkout/success': 'Checkout Success',
        '/profile': 'Profile',
        '/customer/login': 'Customer Login',
        '/customer/signup': 'Customer Signup',
        '/customer/profile': 'Customer Profile',
        // '/admin': 'Admin' // Removed - desktop app used instead
  };
  
  if (pathname.startsWith('/product/')) return 'Product Details';
  if (pathname.startsWith('/collection/')) return 'Collection';
  if (pathname.startsWith('/events/')) return 'Event Details';
  
  return routes[pathname] || 'Unknown Page';
}

function AppLayout({
  navLinks,
  isCartOpen,
  handleCartToggle,
  setIsCartOpen,
  isMobileMenuOpen,
  handleMobileMenuToggle,
  setIsMobileMenuOpen
}) {
  const location = useLocation();
  const isSocialPage = SHOW_ZEE_SOCIAL_FEED && location.pathname.startsWith('/social');

  return (
    <div className="App">
      <AnalyticsTracker />
      <WelcomeModal />
      <TutorialOverlay />
      <TrippyNavbar 
        isCartOpen={isCartOpen} 
        setIsCartOpen={setIsCartOpen}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        handleCartToggle={handleCartToggle}
        handleMobileMenuToggle={handleMobileMenuToggle}
        navLinks={navLinks}
      />
      <SlideOutCart 
        isOpen={isCartOpen} 
        onClose={() => {
          if (process.env.NODE_ENV === 'development') {
            const timestamp = new Date().toISOString();
            console.log('\n' + '='.repeat(80));
            console.log('🔄 [FUNCTION CALL] Cart onClose() called');
            console.log('⏰ [TIMESTAMP]', timestamp);
            console.log('📊 [STATE BEFORE] isCartOpen:', isCartOpen, 'isMobileMenuOpen:', isMobileMenuOpen);
            console.log('='.repeat(80) + '\n');
          }
          logger.logButtonClick('Cart Close');
          setIsCartOpen(false);
        }} 
      />
      <SlideOutMobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => {
          if (process.env.NODE_ENV === 'development') {
            const timestamp = new Date().toISOString();
            console.log('\n' + '='.repeat(80));
            console.log('🔄 [FUNCTION CALL] Menu onClose() called');
            console.log('⏰ [TIMESTAMP]', timestamp);
            console.log('📊 [STATE BEFORE] isMobileMenuOpen:', isMobileMenuOpen, 'isCartOpen:', isCartOpen);
            console.log('='.repeat(80) + '\n');
          }
          logger.logButtonClick('Mobile Menu Close');
          setIsMobileMenuOpen(false);
        }}
        navLinks={navLinks}
      />
      <MusicPlayer suppressPlayback={isSocialPage} hidden={isSocialPage} />
      {SHOW_CHATBOT && <RainbowChatbot />}
    
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/products" element={<Navigate to="/gallery" replace />} />
        <Route path="/products/:productId" element={<Navigate to="/gallery" replace />} />
        <Route path="/product/:slug" element={<Product />} />
        <Route path="/collection/:slug" element={<Collection />} />
        {/* Events temporarily hidden — set SHOW_EVENTS true to restore */}
        <Route path="/events" element={<Navigate to="/" replace />} />
        <Route
          path="/events/:eventSlug"
          element={<EventSignup />}
        />
        <Route
          path="/events-calendar"
          element={SHOW_EVENTS ? <EventsCalendar /> : <Navigate to="/" replace />}
        />
        <Route path="/book-consultation" element={<BookConsultationToContactRedirect />} />
        <Route path="/guidance-call" element={<GuidanceConversation />} />
        <Route path="/connect" element={<Connect />} />
        <Route path="/photos" element={<Navigate to="/gallery" replace />} />
        <Route path="/photos/:eventSlug" element={<Navigate to="/gallery" replace />} />
        <Route path="/content" element={<Content />} />
        <Route path="/content/:contentSlug" element={<Content />} />
        <Route
          path="/social"
          element={SHOW_ZEE_SOCIAL_FEED ? <SocialFeed /> : <Navigate to="/" replace />}
        />
        {/* YouTube/Instagram cross-promo feed — distinct from the legacy /social UGC page above. */}
        <Route path="/zee-social" element={<ZeeSocial />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<CustomCheckout />} />
        <Route path="/checkout/afterpay-return" element={<AfterpayReturn />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/profile" element={<CustomerProfile />} />
        <Route path="/customer/login" element={<CustomerLogin />} />
        <Route path="/customer/signup" element={<CustomerSignup />} />
        <Route path="/customer/profile" element={<CustomerProfileNew />} />
        {/* Music Manager moved to dashboard - Content > Music Manager */}
        {/* Admin route removed - desktop app is used instead */}
        {/* <Route path="/admin" element={<Admin />} /> */}
      </Routes>
    </div>
  );
}

function App() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Define navigation links (shared between navbar and mobile menu)
  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/connect', label: 'Connect' },
    { to: '/zee-social', label: 'Zee Social' },
    ...(SHOW_ZEE_SOCIAL_FEED ? [{ to: '/social', label: 'Zee Social' }] : []),
    ...(SHOW_EVENTS ? [{ to: '/events-calendar', label: 'Book Events' }] : []),
    { to: '/contact', label: 'Contact' },
    { to: '/about', label: 'About' },
    { to: '/policies', label: 'Policies' },
  ];
  
  useEffect(() => {}, []);
  
  // Initialize app - move ALL browser API calls to useEffect
  useEffect(() => {
    // Only run on client side
    if (typeof window !== 'undefined') {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [HYDRATION DEBUG] Running client-side initialization');
      }
      
      logger.logComponentMount('App', { 
        version: '2.0.0',
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      });
      
      logger.log('info', 'system', 'App initialization started', {
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        url: window.location.href
      });
    
      // Track app load
      logger.logAnalytics('app_loaded', {
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      });
    } else {
      console.log('🔍 [HYDRATION DEBUG] Skipping client-side initialization - no window object');
    }
  }, []);
  
  // Log state changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 [STATE] App state updated:', { isCartOpen, isMobileMenuOpen });
      }
      
      logger.log('info', 'system', 'App state updated', {
        isCartOpen,
        isMobileMenuOpen,
        url: window.location.href
      });
    }
  }, [isCartOpen, isMobileMenuOpen]);

  const handleCartToggle = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 handleCartToggle:', { isCartOpen, isMobileMenuOpen, will: !isCartOpen });
    }
    
    logger.logButtonClick('Cart Toggle', { 
      currentState: isCartOpen, 
      newState: !isCartOpen 
    });
    
    // Only close menu when OPENING cart (not when closing)
    if (!isCartOpen && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
    
    setIsCartOpen(!isCartOpen);
  };

  const handleMobileMenuToggle = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 handleMobileMenuToggle:', { isMobileMenuOpen, isCartOpen, will: !isMobileMenuOpen });
    }
    
    logger.logButtonClick('Mobile Menu Toggle', { 
      currentState: isMobileMenuOpen, 
      newState: !isMobileMenuOpen 
    });
    
    // Only close cart when OPENING menu (not when closing)
    if (!isMobileMenuOpen && isCartOpen) {
      setIsCartOpen(false);
    }
    
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <Router>
      <ToastProvider>
        <CustomerAuthProvider>
          <AuthProvider>
            <ReviewProvider>
              <CurrencyProvider>
                <CartProvider>
                  <TutorialProvider>
                    <AppLayout
                    navLinks={navLinks}
                    isCartOpen={isCartOpen}
                    handleCartToggle={handleCartToggle}
                    setIsCartOpen={setIsCartOpen}
                    isMobileMenuOpen={isMobileMenuOpen}
                    handleMobileMenuToggle={handleMobileMenuToggle}
                    setIsMobileMenuOpen={setIsMobileMenuOpen}
                  />
                  </TutorialProvider>
                </CartProvider>
              </CurrencyProvider>
            </ReviewProvider>
          </AuthProvider>
        </CustomerAuthProvider>
      </ToastProvider>
    </Router>
  );
}

export default Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }) => (
    <div style={{ padding: 24 }}>
      <h2>Something went wrong</h2>
      <pre>{error?.message}</pre>
      <button type="button" onClick={resetError}>
        Try again
      </button>
    </div>
  ),
});



